import { Currency } from "@redotech/money/currencies";
import { getCurrencyFormatter } from "@redotech/money/util";
import { ReturnContext } from "@redotech/redo-customer-portal-shared/contexts/return";
import { Subtotal } from "@redotech/redo-customer-portal-shared/types";
import { ShipmentMethodName } from "@redotech/redo-model/draft-return/draft-return";
import { Provider } from "@redotech/redo-model/order";
import { UpsellBillingTrigger } from "@redotech/redo-model/return";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { filterTruthy } from "@redotech/util/array";
import { memo, useContext } from "react";
import { DiscountInputWithButton } from "./discount-input-with-button";
import { IndentedTotals } from "./review/indented-totals";
import * as summaryCss from "./summary-card.module.css";

export const SummaryCard = memo(function SummaryCard({
  labelDeducted,
  shippingFee,
  totalShippingFee,
  pickupFee,
  newOrderValue,
  currency,
  totalPaymentOwed,
  totalRefund,
  totalStoreCredit,
  totalLoyaltyPointsDeducted,
  totalLoyaltyPointsRefunded,
  feesHeader,
  feesOrderHeader,
  adjustmentValue,
  adjustmentValueString,
  returnValue,
  newOrderTaxes,
  newVariantExchangeItemSubtotals,
  newOrderItemsSubtotals,
  productSubtotals,
  taxSubtotals,
  pointsDeductedFromRefundSubtotal,
  submitted,
  isMerchant,
  shippingFeeDeducted,
  provider,
  upsellBillingTrigger,
  exchangeShippingFee,
  exchangeShippingName,
  customerLabelFeeValue,
  customerLabelFeeValueString,
  upsellFeeWaived,
  customerDiscountInputs,
}: {
  feesHeader: string;
  feesOrderHeader: string;
  labelDeducted: boolean;
  shippingFee: number;
  totalShippingFee: number;
  pickupFee?: number;
  newOrderValue: number;
  currency: Currency;
  totalPaymentOwed: number;
  totalRefund: number;
  totalStoreCredit: number;
  adjustmentValue?: number;
  adjustmentValueString?: string;
  returnValue: number;
  newOrderTaxes: Subtotal[];
  newVariantExchangeItemSubtotals: Subtotal[];
  newOrderItemsSubtotals: Subtotal[];
  productSubtotals: Subtotal[];
  taxSubtotals: Subtotal[];
  pointsDeductedFromRefundSubtotal?: Subtotal;
  totalLoyaltyPointsDeducted?: number;
  totalLoyaltyPointsRefunded?: number;
  submitted: boolean;
  isMerchant?: boolean;
  shippingFeeDeducted?: number;
  provider?: Provider;
  upsellBillingTrigger?: UpsellBillingTrigger | null;
  exchangeShippingFee?: number;
  exchangeShippingName?: string;
  customerLabelFeeValue?: number;
  customerLabelFeeValueString?: string;
  upsellFeeWaived?: boolean;
  customerDiscountInputs?: {
    texts: { discountInputLabel: string; discountInputDescription: string };
    inputs: {
      enabled: boolean;
      discountCodes: string[];
      onChangeDiscountCodes: (discountCodes: string[]) => void;
      newDiscountCode: string;
      onChangeNewDiscountCode: (newDiscountCode: string) => void;
    };
  };
}) {
  const currencyFormatter = getCurrencyFormatter(currency);

  // Determine if payment is due immediately based on upsellBillingTrigger
  const isPaymentImmediate =
    upsellBillingTrigger === UpsellBillingTrigger.RETURN_CREATED;

  let owedMinusShipping = totalPaymentOwed;
  if (totalShippingFee) {
    owedMinusShipping = totalPaymentOwed - totalShippingFee;
  }

  const isUpsell = owedMinusShipping > 0;
  const sign = isUpsell ? -1 : 1;

  return (
    <Flex flexDirection="column" gap="xl">
      <Text fontSize="lg" fontWeight="semibold">
        Summary
      </Text>
      {newOrderValue > 0 && isUpsell && (
        <IndentedTotals
          currencyFormatter={currencyFormatter}
          subtotals={[
            ...newOrderItemsSubtotals,
            ...newVariantExchangeItemSubtotals,
            ...newOrderTaxes,
          ]}
          title={feesOrderHeader}
          total={newOrderValue}
        />
      )}
      <IndentedTotals
        currencyFormatter={currencyFormatter}
        subtotals={filterTruthy([
          ...productSubtotals,
          ...taxSubtotals,
          pointsDeductedFromRefundSubtotal,
        ])}
        title={feesHeader}
        total={returnValue * sign}
      />
      {adjustmentValue && adjustmentValueString && adjustmentValue !== 0 ? (
        <Flex dir="row" justify="space-between">
          <Text fontSize="sm">{adjustmentValueString}</Text>
          <Text fontSize="sm" textColor="secondary">
            {currencyFormatter.format(adjustmentValue * sign)}
          </Text>
        </Flex>
      ) : null}
      {customerLabelFeeValue &&
      customerLabelFeeValueString &&
      customerLabelFeeValue !== 0 ? (
        <Flex dir="row" justify="space-between">
          <Text fontSize="sm">{customerLabelFeeValueString}</Text>
          <Text fontSize="sm" textColor="secondary">
            {currencyFormatter.format(customerLabelFeeValue)}
          </Text>
        </Flex>
      ) : null}
      {newOrderValue > 0 && !isUpsell && (
        <IndentedTotals
          currencyFormatter={currencyFormatter}
          subtotals={[
            ...newOrderItemsSubtotals,
            ...newVariantExchangeItemSubtotals,
            ...newOrderTaxes,
          ]}
          title={feesOrderHeader}
          total={newOrderValue * -1}
        />
      )}
      {labelDeducted && (
        <ShippingFees
          currency={currency}
          exchangeShippingFee={exchangeShippingFee}
          exchangeShippingName={exchangeShippingName}
          negative
          pickupFee={pickupFee}
          shippingFee={shippingFeeDeducted ?? shippingFee}
        />
      )}
      <Divider />
      {owedMinusShipping > 0 && (
        <>
          <Flex dir="row" justify="space-between">
            <Text fontSize="sm">
              {isPaymentImmediate
                ? submitted
                  ? "Paid"
                  : "Due now"
                : "Due later"}
            </Text>
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              {currencyFormatter.format(
                upsellFeeWaived ? 0 : owedMinusShipping,
              )}
            </Text>
          </Flex>
          {!(
            submitted &&
            upsellBillingTrigger === UpsellBillingTrigger.RETURN_CREATED
          ) &&
            !upsellFeeWaived && (
              <Text fontSize="sm" textColor="primary">
                {isPaymentImmediate ? (
                  <>
                    {`${isMerchant ? "The customer" : "You"} will be charged `}
                    <strong>
                      {currencyFormatter.format(owedMinusShipping)}
                    </strong>
                    {` immediately for ${isMerchant ? "their" : "your"} exchange`}
                  </>
                ) : (
                  <>
                    {`${isMerchant ? "The customer" : "You"} will be sent an invoice for the remaining `}
                    <strong>
                      {currencyFormatter.format(owedMinusShipping)}
                    </strong>
                    {` balance for ${isMerchant ? "their" : "your"} new items once ${isMerchant ? "the" : "your"} return has been handled`}
                  </>
                )}
              </Text>
            )}
        </>
      )}
      {totalStoreCredit > 0 && (
        <Flex dir="row" justify="space-between">
          <Text fontSize="sm" textColor="secondary">
            Store credit
          </Text>
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            {currencyFormatter.format(totalStoreCredit)}
          </Text>
        </Flex>
      )}
      {totalRefund > 0 && (
        <Flex dir="row" justify="space-between">
          <Text fontSize="sm" textColor="secondary">
            Refund amount
          </Text>
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            {currencyFormatter.format(totalRefund)}
          </Text>
        </Flex>
      )}
      {totalRefund <= 0 && totalStoreCredit <= 0 && owedMinusShipping <= 0 && (
        <Flex dir="row" justify="space-between">
          <Text fontSize="sm">Subtotal</Text>
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            {currencyFormatter.format(0)}
          </Text>
        </Flex>
      )}

      {totalShippingFee > 0 && !labelDeducted && (
        <>
          <Divider classes={summaryCss.fullDivider} />
          <Text fontSize="sm" fontWeight="semibold">
            Shipping
          </Text>
          <ShippingFees
            currency={currency}
            exchangeShippingFee={exchangeShippingFee}
            exchangeShippingName={exchangeShippingName}
            pickupFee={pickupFee}
            shippingFee={shippingFee}
          />
          <Divider />

          <Flex dir="row" justify="space-between">
            <Text fontSize="sm">{submitted ? "Paid" : "Due now"}</Text>
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              {currencyFormatter.format(totalShippingFee)}
            </Text>
          </Flex>
        </>
      )}

      {!!totalLoyaltyPointsDeducted && (
        <Flex dir="row" justify="space-between">
          <Text fontSize="sm" textColor="secondary">
            Loyalty Points Deducted
          </Text>
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            -{totalLoyaltyPointsDeducted}
          </Text>
        </Flex>
      )}
      {!!totalLoyaltyPointsRefunded && (
        <Flex dir="row" justify="space-between">
          <Text fontSize="sm" textColor="secondary">
            Loyalty Points Refunded
          </Text>
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            {totalLoyaltyPointsRefunded}
          </Text>
        </Flex>
      )}

      {customerDiscountInputs && !isMerchant && (
        <DiscountInputWithButton
          description={customerDiscountInputs.texts.discountInputDescription}
          discountCodes={customerDiscountInputs.inputs.discountCodes}
          enabled={customerDiscountInputs.inputs.enabled}
          label={customerDiscountInputs.texts.discountInputLabel}
          newDiscountCode={customerDiscountInputs.inputs.newDiscountCode}
          onChangeDiscountCodes={
            customerDiscountInputs.inputs.onChangeDiscountCodes
          }
          onChangeNewDiscountCode={
            customerDiscountInputs.inputs.onChangeNewDiscountCode
          }
        />
      )}
    </Flex>
  );
});

export const ShippingFees = memo(function ShippingFees({
  shippingFee,
  pickupFee,
  currency,
  negative,
  exchangeShippingFee,
  exchangeShippingName,
}: {
  shippingFee: number;
  currency: Currency;
  pickupFee?: number;
  negative?: boolean;
  exchangeShippingFee?: number;
  exchangeShippingName?: string;
}) {
  const returnContext = useContext(ReturnContext);
  const currencyFormatter = getCurrencyFormatter(currency);
  const isFedexEasyReturn =
    returnContext.draftReturn?.shipmentMethod?.method ===
    ShipmentMethodName.BLUE_YONDER;
  const isReturnBearDropoffReturn =
    returnContext.draftReturn?.shipmentMethod?.method ===
    ShipmentMethodName.RETURN_BEAR;

  const isConvenienceFee = isFedexEasyReturn || isReturnBearDropoffReturn;
  return (
    <>
      <Flex dir="row" justify="space-between">
        <Text fontSize="sm" textColor="secondary">
          {isConvenienceFee ? "Convenience fee" : "Return shipping"}
        </Text>
        <Text fontSize="sm" textColor="secondary">
          {`${negative ? "-" : ""}${currencyFormatter.format(shippingFee)}`}
        </Text>
      </Flex>
      {pickupFee && (
        <Flex dir="row" justify="space-between">
          <Text fontSize="sm" textColor="secondary">
            Package pickup
          </Text>
          <Text fontSize="sm" textColor="secondary">
            {`${negative ? "-" : ""}${currencyFormatter.format(pickupFee)}`}
          </Text>
        </Flex>
      )}
      {exchangeShippingFee && exchangeShippingFee > 0 ? (
        <Flex dir="row" justify="space-between">
          <Text fontSize="sm" textColor="secondary">
            {`New order shipping (${exchangeShippingName})`}
          </Text>
          <Text fontSize="sm" textColor="secondary">
            {`${negative ? "-" : ""}${currencyFormatter.format(exchangeShippingFee)}`}
          </Text>
        </Flex>
      ) : null}
    </>
  );
});
